<template>
  <div class="admin-shell">

    <!-- Top Navbar -->
    <nav class="admin-topbar d-flex align-items-center px-3">
      <button class="sidebar-toggle-btn mr-3" @click="sidebarOpen = !sidebarOpen" title="Toggle sidebar">
        <i class="fas fa-bars"></i>
      </button>
      <span class="topbar-brand">
        <i class="fas fa-shield-halved mr-2"></i>Admin Panel
      </span>
      <div class="ml-auto d-flex align-items-center" v-if="userInfo">
        <div class="admin-avatar mr-2">{{ initials }}</div>
        <div class="topbar-user-info mr-3 d-none d-md-block">
          <div class="topbar-user-name">{{ userInfo.firstName }} {{ userInfo.lastName }}</div>
          <div class="topbar-user-role">Administrator</div>
        </div>
        <button class="btn btn-sm btn-outline-light" @click="logout" title="Logout">
          <i class="fas fa-right-from-bracket"></i>
          <span class="d-none d-md-inline ml-1">Logout</span>
        </button>
      </div>
    </nav>

    <!-- Body -->
    <div class="admin-body">

      <!-- Sidebar -->
      <aside class="admin-sidebar custom-scroller" :class="{ collapsed: !sidebarOpen }">
        <nav class="sidebar-nav">
          <div
            v-for="item in visibleNavItems" :key="item.component"
            class="sidebar-item"
            :class="{ active: componentId === item.component }"
            @click="switchComponent(item.component)"
            :title="!sidebarOpen ? item.label : ''"
          >
            <i :class="['sidebar-icon', item.icon]"></i>
            <span class="sidebar-label">{{ item.label }}</span>
            <span v-if="componentId === item.component" class="active-pill"></span>
          </div>
        </nav>
      </aside>

      <!-- Main content -->
      <main class="admin-main">
        <div class="content-header d-flex align-items-center mb-2">
          <div>
            <div class="content-title">{{ activeItem.label }}</div>
            <div class="content-breadcrumb">
              <span class="text-muted">Admin</span>
              <i class="fas fa-chevron-right mx-1 text-muted" style="font-size:10px;"></i>
              <span>{{ activeItem.label }}</span>
            </div>
          </div>
        </div>
        <div class="content-card">
          <component :is="componentId"></component>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import UserList from "../components/Admin/UserList.vue";
import EventList from "../components/Admin/EventList.vue";
import ChangePassword from "../components/UserProfile/ChangePassword.vue";
import Profile from "./Profile.vue";
import MailingList from "../components/Admin/MailingList.vue";
import NewsAdminList from "../components/Admin/NewsList.vue";
import EmailTemplateManager from "../components/Admin/EmailTemplateManager.vue";
import GateScanner from "../components/Admin/GateScanner.vue";
import * as getLogout from "../GeneralScripts/Logout";

export default {
  name: "AdminPanel",
  components: {
    UserList,
    EventList,
    ChangePassword,
    Profile,
    MailingList,
    NewsAdminList,
    EmailTemplateManager,
    GateScanner,
  },
  data() {
    return {
      userInfo: null,
      componentId: "UserList",
      sidebarOpen: true,
      navItems: [
        { component: "UserList",             label: "Users",           icon: "fas fa-users" },
        { component: "EventList",            label: "Events",          icon: "fas fa-calendar-days" },
        { component: "MailingList",          label: "Mailing List",    icon: "fas fa-envelope-open-text" },
        { component: "NewsAdminList",        label: "News",            icon: "fas fa-newspaper" },
        { component: "EmailTemplateManager", label: "Email Templates", icon: "fas fa-file-code" },
        { component: "GateScanner",          label: "Gate Scanner",    icon: "fas fa-qrcode" },
        { component: "Profile",              label: "Profile",         icon: "fas fa-circle-user" },
      ],
    };
  },
  computed: {
    watchUserInfo() {
      return this.$store.getters.LoggedUserInfo;
    },
    isGateKeeper() {
      return this.userInfo?.type === 'GATEKEEPER';
    },
    visibleNavItems() {
      if (this.isGateKeeper) {
        return this.navItems.filter((item) => item.component === 'GateScanner');
      }
      return this.navItems;
    },
    activeItem() {
      return this.navItems.find((n) => n.component === this.componentId) || { label: "" };
    },
    initials() {
      if (!this.userInfo) return "";
      return ((this.userInfo.firstName || "")[0] || "") + ((this.userInfo.lastName || "")[0] || "");
    },
  },
  watch: {
    watchUserInfo(newData) {
      this.userInfo = newData;
      if (newData?.type === 'GATEKEEPER') this.componentId = 'GateScanner';
    },
  },
  created() {
    this.userInfo = this.$store.getters.LoggedUserInfo;
    if (this.userInfo?.type === 'GATEKEEPER') this.componentId = 'GateScanner';
    if (window.innerWidth < 768) this.sidebarOpen = false;
  },
  methods: {
    switchComponent(compo) {
      this.componentId = compo;
      if (window.innerWidth < 768) this.sidebarOpen = false;
    },
    logout() {
      getLogout.Logout();
    },
  },
};
</script>

<style scoped>
/* Shell */
.admin-shell {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f0f2f8;
  overflow: hidden;
}

/* Topbar */
.admin-topbar {
  height: 56px;
  min-height: 56px;
  background: linear-gradient(135deg, #2d4fa1 0%, #1a336e 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(29, 51, 110, 0.25);
  z-index: 100;
  flex-shrink: 0;
}
.topbar-brand {
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: 0.3px;
  white-space: nowrap;
}
.sidebar-toggle-btn {
  background: rgba(255, 255, 255, 0.12);
  border: none;
  color: #fff;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}
.sidebar-toggle-btn:hover { background: rgba(255, 255, 255, 0.22); }
.admin-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}
.topbar-user-name { font-size: 13px; font-weight: 600; line-height: 1.2; color: #fff; }
.topbar-user-role { font-size: 11px; color: rgba(255,255,255,0.65); line-height: 1.2; }

/* Body */
.admin-body { display: flex; flex: 1; overflow: hidden; }

/* Sidebar */
.admin-sidebar {
  width: 220px;
  min-width: 220px;
  background: #fff;
  border-right: 1px solid #e4e8f0;
  overflow-y: auto;
  overflow-x: hidden;
  transition: width 0.25s ease, min-width 0.25s ease;
  flex-shrink: 0;
  padding: 12px 8px;
}
.admin-sidebar.collapsed { width: 56px; min-width: 56px; }
.sidebar-nav { display: flex; flex-direction: column; gap: 2px; }
.sidebar-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  color: #4a5568;
  font-size: 13.5px;
  font-weight: 500;
  position: relative;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  overflow: hidden;
}
.sidebar-item:hover { background: #f0f4ff; color: #2d4fa1; }
.sidebar-item.active { background: #eef2ff; color: #2d4fa1; font-weight: 700; }
.sidebar-icon { font-size: 15px; width: 20px; text-align: center; flex-shrink: 0; }
.sidebar-label { flex: 1; transition: opacity 0.2s; }
.admin-sidebar.collapsed .sidebar-label { opacity: 0; pointer-events: none; width: 0; }
.active-pill {
  width: 4px; height: 20px;
  background: #2d4fa1;
  border-radius: 4px;
  position: absolute;
  right: 6px;
}
.admin-sidebar.collapsed .active-pill { display: none; }

/* Main */
.admin-main {
  flex: 1;
  overflow: hidden;
  padding: 16px 20px 16px 16px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.content-title { font-size: 1.15rem; font-weight: 700; color: #1a336e; line-height: 1.2; }
.content-breadcrumb { font-size: 12px; color: #718096; margin-top: 2px; }
.content-card {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
  flex: 1;
  overflow: scroll;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
</style>

<!-- Make every admin list component fill the card and scroll internally -->
<style>
.content-card > [id$="-list"],
.content-card > #event-list,
.content-card > #user-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
/* Card-style lists (EventList, NewsList) — .list scrolls */
.content-card > [id$="-list"] .list,
.content-card > #event-list .list,
.content-card > #user-list .list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}
/* DataTable wrapper fills remaining height */
.content-card #CoreDataTable {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
/* EmailTemplateManager fills card too */
.content-card .etm-shell {
  height: 100%;
  min-height: 0;
}
</style>